import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getFirmById } from "../../Services/Firm";
import { getFirmContactByFirmId } from "../../Services/FirmContact";
import { hideLoader, showLoader } from "../../redux/loaderActions";
import { manageFirmSlice } from "../../redux/states/manageFirm.state";

export type ManageFirmContactLocationState = Record<string, any> & {
  id: string;
  name?: string;
  firmContact: any;
  jurisdictionList?: any[];
  personList?: any[];
  selectedTab?: string;
};

function asFirmContactList(data: unknown): any[] {
  if (data == null) {
    return [];
  }
  return Array.isArray(data) ? data : [data];
}

function hasValidFirmContactObject(fc: unknown): boolean {
  return fc != null && typeof fc === "object" && !Array.isArray(fc);
}

/** Cosmos/API payload normalization so editors never see undefined .contacts */
export function normalizeFirmContactShape(fc: any) {
  if (!hasValidFirmContactObject(fc)) {
    return fc;
  }
  return {
    ...fc,
    contacts: Array.isArray(fc.contacts) ? fc.contacts : [],
    jurisdictions:
      fc.jurisdictions === undefined
        ? undefined
        : Array.isArray(fc.jurisdictions)
          ? fc.jurisdictions
          : [],
  };
}

function buildEmptyJurisdictionSlots(firm: any, jurisdictionList: any[]) {
  if (!firm?.jurisdictionGroup || firm.membershipType === "Primary") {
    return [];
  }
  return jurisdictionList
    .filter(
      (j) =>
        j?.jurisdictionGroup &&
        j.jurisdictionGroup === firm.jurisdictionGroup
    )
    .map((j) => ({ jurisdiction: j.id, contacts: [] as any[] }));
}

export function buildPlaceholderFirmContact(
  firm: any,
  jurisdictionList: any[]
) {
  const isPrimary = firm?.membershipType === "Primary";
  const shells = buildEmptyJurisdictionSlots(firm, jurisdictionList);
  return {
    id: "",
    firm: firm.id,
    firmName: firm.firmName ?? firm.name,
    level: isPrimary,
    contacts: [] as any[],
    jurisdictions: isPrimary ? undefined : shells,
    office: "",
    person: "",
  };
}

/**
 * Resolves Manage Firm → Firm Contact page data from router state when complete,
 * otherwise loads firm + firm contact by URL id (direct link / refresh / MANAGE with missing FC).
 */
export function useManageFirmContactPageState(firmId: string | undefined) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [mergedState, setMergedState] =
    useState<ManageFirmContactLocationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const rs = location.state as ManageFirmContactLocationState | undefined;

    if (!firmId) {
      setLoading(false);
      setError("Missing firm id");
      setMergedState(null);
      return;
    }

    // Fast path: navigated from General Tab with a real firm contact document
    if (
      rs?.id === firmId &&
      rs?.name &&
      hasValidFirmContactObject(rs.firmContact)
    ) {
      const normalized = normalizeFirmContactShape(rs.firmContact);

      const next: ManageFirmContactLocationState = {
        ...rs,
        firmContact: normalized,
        selectedTab: rs.selectedTab ?? "GeneralTab",
      };
      dispatch(manageFirmSlice.actions.SetFirm(rs));
      setMergedState(next);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      showLoader();
      try {
        const firm = await getFirmById(firmId);
        if (!firm || cancelled) {
          return;
        }

        dispatch(manageFirmSlice.actions.SetFirm(firm));

        const fcResponse = await getFirmContactByFirmId(firmId);
        const list = asFirmContactList(fcResponse);
        const raw = list[0];
        const fcEntity = raw
          ? normalizeFirmContactShape(raw)
          : buildPlaceholderFirmContact(firm, rs?.jurisdictionList ?? []);

        const next: ManageFirmContactLocationState = {
          ...firm,
          ...rs,
          id: firm.id,
          name: firm.name ?? firm.firmName,
          selectedTab: "GeneralTab",
          firmContact: fcEntity,
          personList: rs?.personList ?? [],
        };

        if (cancelled) {
          return;
        }

        setMergedState(next);
        navigate(location.pathname, { replace: true, state: next });
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message ?? "Failed to load firm contact");
          setMergedState(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          hideLoader();
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    firmId,
    location.pathname,
    location.key,
    location.state,
    navigate,
    dispatch,
  ]);

  return { mergedState, loading, error };
}
